import * as React from 'react';
import type { OnlineSemester } from '../../../Semester/OnlineSemester.csharp';
import type { ParentStudyViewModel } from '../../../Semester/ParentStudyViewModel';
import { Txt } from '../../Txt';
import { ElectiveItem } from '../ElectiveItem/ElectiveItem';
import { Subject } from '../Subject/Subject';
import type { OnlineSemesterItemViewModel } from '../SubjectsBySemester';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { moneyToString } from '~/Partials/Utils/utils';
import styles from './Semester.module.css';
import { useSemesterModel } from './SemesterModel';

export interface OnlineSemesterViewModel extends OnlineSemester {
    index: number;
    items: OnlineSemesterItemViewModel[];
}

export interface SemesterProps {
    semesterInitial: OnlineSemesterViewModel;
    parentStudy: ParentStudyViewModel;
}

export const Semester: React.FC<React.PropsWithChildren<SemesterProps>> = ({ semesterInitial, parentStudy }) => {
    const model = useSemesterModel(semesterInitial, semesterInitial.items, parentStudy);
    const totalPrice = model.getTotalPriceOfSemester();
    const totalPoints = model.getTotalPointsOfSemester();
    const creditsLabel = model.getCreditsLabel();
    const isSemesterAdded = model.isSemesterAddedToBasket();

    const renderItem = (semester: OnlineSemesterViewModel, item: OnlineSemesterItemViewModel) =>
        item.electivePointsAvailable ? (
            <ElectiveItem
                key={`${parentStudy.timeType}_${semester.index}_${item.index}`}
                parentStudy={parentStudy}
                semesterIndex={semester.index}
                isSemesterAdded={isSemesterAdded}
                item={item}
                setItemState={model.setItemState}
            />
        ) : (
            <Subject key={item.index} {...item.obligatorySubject} />
        );

    return (
        <li key={semesterInitial.index} className={styles.Semester}>
            <h3 className={styles.Heading}>
                {semesterInitial.index + 1}. <Txt id="semester" />
            </h3>
            <ul className={styles.ItemList}>
                {!!model.items.length && model.items.map(item => renderItem(semesterInitial, item))}
            </ul>
            <div className={styles.Order}>
                <div>
                    <span className={styles.Points}>
                        <Txt
                            id="totalPointsInfo"
                            values={{
                                credits: totalPoints,
                                creditsName: creditsLabel,
                            }}
                        />
                    </span>
                    <span className={styles.Price}>{moneyToString(totalPrice)}</span>
                </div>
                {isSemesterAdded ? (
                    <>
                        <Button
                            className={styles.RemoveButton}
                            onClick={() => model.removeSemesterFromBasket()}
                            disabled={model.hasSemesterUnusedElectivePoints()}
                            tier={ButtonTier.LegacyPrimary}
                            type={ButtonType.Outline}
                            size={ButtonSize.Large}
                        >
                            <Txt id="remove" />
                        </Button>
                        <a href="#subjectBasket" className={styles.SkipLink}>
                            <Txt id="goToBasket" />
                        </a>
                    </>
                ) : (
                    <Button
                        onClick={() => {
                            model.addSemesterToBasket();
                        }}
                        disabled={model.hasSemesterUnusedElectivePoints()}
                        className={styles.OrderButton}
                        tier={ButtonTier.LegacyPrimary}
                        type={ButtonType.Solid}
                        size={ButtonSize.Large}
                    >
                        <Txt id="order" />
                    </Button>
                )}
            </div>
        </li>
    );
};
